import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { rovingTabIndex, useRovingTabs } from '../components/useRovingTabs';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowPathIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  CheckIcon,
  DocumentDuplicateIcon,
  ExclamationTriangleIcon,
  HandThumbUpIcon,
  PlayCircleIcon,
  Square2StackIcon,
  StarIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import {
  DEDUP_THRESHOLD_DEFAULT,
  DEDUP_THRESHOLD_MAX,
  DEDUP_THRESHOLD_MIN,
  DEDUP_CLUSTERS_LIMIT_DEFAULT,
  type DedupAction,
  type DedupCluster,
  type DedupDecision,
  type DedupMember,
} from '@xphoto/shared';
import { useI18n } from '../i18n';
import { useAuth } from '../auth';
import { ApiError } from '../gallery/api';
import { LIBRARY_ID } from '../gallery/config';
import { interpolate } from '../gallery/format';
import { dirnameOf, formatBytes, formatDuration } from '../viewer/format';
import { withSlotParam } from '../lib/activeSlot';
import { recordAction } from '../diagnostics/buffers';
import { Portal } from '../components/Portal';
import { usePageReset } from '../components/page-reset';
import { useRefreshAfterJob } from '../notifications/use-refresh-after-job';
import { clearScrollStash, saveScrollStash, useScrollRestore } from '../lib/scroll-restore';
import { openViewerSeries } from '../viewer/ViewerRoute';
import { applyDedup, dismissClusters, fetchClusters, fetchSummary, runDedup } from './dedup-api';
import { DedupConfirmModal } from './DedupConfirmModal';
import { ExactCopiesSection, type ExactChoices } from './ExactCopiesSection';

/**
 * Écran DÉDOUBLONNAGE — PLANCHE DE REVUE (T6b, XPHOTO-08/-09/-23 ; refonte 0.45.0 ;
 * onglets 0.57.0).
 *
 * PAGE (comme l'Outil EXIF depuis 0.18.0) rendue dans la zone `main` sous l'en-tête
 * persistant (`AppChrome`) — le fil d'Ariane (« Accueil / Dédoublonnage ») reste
 * visible et pilote le retour. Défile via son propre conteneur `overflow-y-auto`.
 *
 * DEUX ONGLETS (logiques distinctes, ne plus empiler) :
 *  - « Copies identiques » (`ExactCopiesSection`) : fichiers pixel-identiques présents à
 *    plusieurs chemins (inventaire du scan). Choix d'UN chemin à garder par image.
 *  - « Images similaires » (clusters pHash) : quasi-doublons. Décision par image
 *    Conserver / Effacer (défaut Conserver → rien n'est supprimé sans action).
 *
 * L'onglet ACTIF est dans l'URL (`?onglet=identiques|similaires`, clé FR, défaut
 * `identiques`) → adressable et RESTAURÉ au retour de la visionneuse. Le reset « fil
 * d'Ariane » navigue vers `/outils/dedup` PROPRE (query retirée → onglet par défaut) et
 * remet l'état local + le défilement en haut.
 *
 * REFONTE (retour utilisateur) — le regroupement pHash produit des FAUX POSITIFS
 * (images sans rapport groupées). L'ancien modèle « UN seul keeper par cluster » était
 * faux dès que les images sont réellement DIFFÉRENTES. On passe donc à un choix par
 * image (Conserver / Effacer), défaut Conserver.
 *
 * GARDE-FOU « ≥ 1 conservé par cluster » (impossible de tout supprimer d'un cluster) :
 * dès qu'un cluster n'a plus qu'UN membre en Conserver, l'option « Effacer » de CE
 * membre est DÉSACTIVÉE → l'état « 0 conservé » n'est pas représentable.
 */

/** Onglets de la planche : copies identiques (défaut) ou images similaires. */
type DedupTab = 'identiques' | 'similaires';
const TAB_PARAM = 'onglet';
const DEFAULT_TAB: DedupTab = 'identiques';

/** Lit l'onglet de l'URL, tolérant (valeur inconnue → défaut). */
function parseTab(raw: string | null): DedupTab {
  return raw === 'similaires' ? 'similaires' : DEFAULT_TAB;
}

interface Feedback {
  kind:
    | 'runQueued'
    | 'applyRunning'
    | 'applyQueued'
    | 'runError'
    | 'applyError'
    | 'writeDisabled'
    | 'dismissError'
    | 'dismissForbidden';
}

/** Décision par membre au sein d'un cluster : clé = `assetId`. Absent ⇒ Conserver. */
type ClusterDecisions = Map<string, DedupAction>;

/**
 * Clé de RÉSERVE de défilement (`scroll-restore`) pour cette page. Écrite JUSTE avant
 * d'ouvrir la visionneuse (depuis l'un ou l'autre onglet), relue UNE FOIS au remontage →
 * l'écran reprend au même onglet, à la même page, au même défilement, avec les décisions.
 */
const DEDUP_SCROLL_KEY = 'dedup-review';

/**
 * Supplément sérialisable de la réserve : l'onglet actif + la page + les décisions des
 * DEUX onglets. Les `Map` ne sont pas sérialisables JSON telles quelles → projetées en
 * tableaux imbriqués (et réciproquement).
 */
interface DedupStashExtra {
  tab: DedupTab;
  // Onglet « Images similaires ».
  similarPage: number;
  similarChoices: [string, [string, DedupAction][]][];
  // Onglet « Copies identiques ».
  exactPage: number;
  exactChoices: [string, string][];
}

/** Sérialise les décisions « similaires » (Map imbriquée) en tableaux JSON. */
function serializeSimilar(
  choices: Map<string, ClusterDecisions>,
): DedupStashExtra['similarChoices'] {
  return [...choices].map(([clusterId, members]) => [clusterId, [...members]]);
}

/** Reconstitue les décisions « similaires » depuis leur forme sérialisée. */
function deserializeSimilar(raw: DedupStashExtra['similarChoices']): Map<string, ClusterDecisions> {
  return new Map(raw.map(([clusterId, members]) => [clusterId, new Map(members)]));
}

export function DedupReviewPage(): React.JSX.Element {
  const { t, tp } = useI18n();
  const navigate = useNavigate();
  const { user } = useAuth();
  const libraryId = user?.libraryId ?? LIBRARY_ID;

  const [searchParams, setSearchParams] = useSearchParams();
  const tab = parseTab(searchParams.get(TAB_PARAM));
  const onTabsKeyDown = useRovingTabs();

  const setTab = useCallback(
    (next: DedupTab) => {
      setSearchParams(
        (prev) => {
          const sp = new URLSearchParams(prev);
          if (next === DEFAULT_TAB) sp.delete(TAB_PARAM);
          else sp.set(TAB_PARAM, next);
          return sp;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Onglet en attente de propagation vers l'URL, posé par la RESTAURATION (retour de la
  // visionneuse). Écrire les `searchParams` DIRECTEMENT depuis le `useLayoutEffect` de
  // `useScrollRestore` est avalé par le routeur (navigation planifiée en couche layout au
  // montage) ; on passe donc par un `useEffect` NORMAL déclenché par ce state.
  const [queuedTab, setQueuedTab] = useState<DedupTab | null>(null);
  useEffect(() => {
    if (queuedTab === null) return;
    setTab(queuedTab);
    setQueuedTab(null);
  }, [queuedTab, setTab]);

  const [threshold, setThreshold] = useState(DEDUP_THRESHOLD_DEFAULT);
  const [runBusy, setRunBusy] = useState(false);
  const [applyBusy, setApplyBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  // Bumpé → recharge clusters + résumé (après analyse actualisée ou suppression).
  const [refreshKey, setRefreshKey] = useState(0);
  // Bumpé pour recharger UNIQUEMENT le résumé (badge/compteurs), sans recharger la liste des
  // clusters : après un écartement, la liste reste pilotée par le retrait OPTIMISTE.
  const [summaryRefreshKey, setSummaryRefreshKey] = useState(0);
  const [page, setPage] = useState(0);

  // Décisions par membre (onglet « Images similaires »), indexées par `clusterId` puis
  // `assetId`. Un `assetId` ABSENT vaut « Conserver » (défaut) → on ne stocke QUE les
  // mises « Effacer ».
  const [choices, setChoices] = useState<Map<string, ClusterDecisions>>(new Map());

  // Clusters « faux positifs » écartés localement (retrait OPTIMISTE de la liste avant que le
  // rechargement ne les fasse disparaître côté serveur). Purgé au rechargement (`refreshKey`).
  const [dismissedIds, setDismissedIds] = useState<Set<string>>(new Set());
  // Clusters dont l'écartement est EN COURS (spinner + bouton désactivé sur la carte).
  const [dismissingIds, setDismissingIds] = useState<Set<string>>(new Set());

  // État piloté de l'onglet « Copies identiques » (lifté ici pour la restauration).
  const [exactPage, setExactPage] = useState(0);
  const [exactChoices, setExactChoices] = useState<ExactChoices>(new Map());
  const [exactReady, setExactReady] = useState(false);

  const limit = DEDUP_CLUSTERS_LIMIT_DEFAULT;
  const offset = page * limit;

  // Conteneur DÉFILANT de la page — cible de la restauration au retour de la visionneuse.
  const scrollRef = useRef<HTMLDivElement>(null);

  const clustersState = useClusters(libraryId, limit, offset, refreshKey);
  const summaryState = useSummary(libraryId, refreshKey + summaryRefreshKey);

  // Données brutes du serveur. La liste AFFICHÉE en retire les clusters écartés localement
  // (retrait optimiste, avant que le rechargement ne les fasse disparaître côté serveur).
  const rawClusters = clustersState.data?.clusters;
  const clusters = useMemo(
    () => rawClusters?.filter((c) => !dismissedIds.has(c.id)),
    [rawClusters, dismissedIds],
  );

  // Un nouveau lot de données du serveur SUPERSÈDE les retraits optimistes (le serveur exclut
  // déjà les clusters écartés) → on repart d'ensembles vides pour ne pas masquer à tort.
  useEffect(() => {
    setDismissedIds(new Set());
    setDismissingIds(new Set());
  }, [clustersState.data]);

  // « prêt » pour la restauration du défilement = l'onglet ACTIF a ses données disposées.
  const restoreReady = tab === 'similaires' ? clustersState.status === 'ready' : exactReady;

  // Restauration « endroit dans la liste » au retour de la visionneuse : rejoue onglet +
  // pages + décisions des deux onglets, puis repositionne le défilement une fois l'onglet
  // actif chargé et disposé. La réserve n'existe QUE si l'on vient d'ouvrir la visionneuse
  // depuis cet écran → une entrée fraîche ne restaure rien (lecture destructive).
  useScrollRestore<DedupStashExtra>(
    DEDUP_SCROLL_KEY,
    scrollRef,
    restoreReady,
    useCallback((extra: DedupStashExtra) => {
      // L'onglet est propagé à l'URL via `queuedTab` + `useEffect` (voir plus haut) : le
      // faire ici (couche layout) serait avalé par le routeur.
      setQueuedTab(extra.tab);
      setPage(extra.similarPage);
      setChoices(deserializeSimilar(extra.similarChoices));
      setExactPage(extra.exactPage);
      setExactChoices(new Map(extra.exactChoices));
    }, []),
  );

  const setDecision = useCallback((clusterId: string, assetId: string, action: DedupAction) => {
    setChoices((prev) => {
      const next = new Map(prev);
      const cluster = new Map(next.get(clusterId) ?? []);
      if (action === 'delete') cluster.set(assetId, 'delete');
      else cluster.delete(assetId); // « Conserver » = défaut → on retire l'entrée
      if (cluster.size === 0) next.delete(clusterId);
      else next.set(clusterId, cluster);
      return next;
    });
  }, []);

  // Décision effective d'un membre : « delete » s'il est explicitement marqué, sinon « keep ».
  const decisionOf = useCallback(
    (clusterId: string, assetId: string): DedupAction =>
      choices.get(clusterId)?.get(assetId) === 'delete' ? 'delete' : 'keep',
    [choices],
  );

  // Nombre de membres CONSERVÉS d'un cluster (garde-fou : si 1, on désactive l'effacement
  // du dernier conservé).
  const keptCountOf = useCallback(
    (cluster: DedupCluster): number => {
      const deleted = choices.get(cluster.id);
      if (!deleted || deleted.size === 0) return cluster.members.length;
      return cluster.members.reduce(
        (acc, m) => acc + (deleted.get(m.assetId) === 'delete' ? 0 : 1),
        0,
      );
    },
    [choices],
  );

  // Décisions PLATES dérivées des choix, sur les clusters de la PAGE COURANTE uniquement.
  const decisions = useMemo<DedupDecision[]>(() => {
    if (!clusters) return [];
    const out: DedupDecision[] = [];
    for (const cluster of clusters) {
      const deleted = choices.get(cluster.id);
      if (!deleted || deleted.size === 0) continue; // aucun delete pour ce cluster
      for (const member of cluster.members) {
        out.push({
          assetId: member.assetId,
          action: deleted.get(member.assetId) === 'delete' ? 'delete' : 'keep',
        });
      }
    }
    return out;
  }, [clusters, choices]);

  const deleteCount = useMemo(
    () => decisions.filter((d) => d.action === 'delete').length,
    [decisions],
  );

  // Auto-rafraîchissement APRÈS la fin du job de suppression (`dedup_apply`). Recharger
  // juste après l'enfilage renverrait les MÊMES membres (job pas encore fini) → on attend
  // la fin du job (front descendant ou repli par délai) pour recharger.
  const onApplyJobFinished = useCallback(() => {
    setChoices(new Map());
    setRefreshKey((k) => k + 1);
    setFeedback({ kind: 'applyQueued' });
  }, []);
  const {
    active: applyActive,
    pending: applyPending,
    markPending: markApplyPending,
  } = useRefreshAfterJob(libraryId, 'dedup_apply', onApplyJobFinished);
  const applyInProgress = applyPending || applyActive;

  const onClose = useCallback(() => navigate('/'), [navigate]);

  // Réinitialisation « fil d'Ariane » : vide les choix des DEUX onglets, revient page 0,
  // remet le seuil par défaut, remonte en haut. Déclenchée par un clic sur le maillon
  // « Dédoublonnage » (la navigation vers `/outils/dedup` PROPRE remet aussi l'onglet par
  // défaut ; ce rappel couvre l'état LOCAL, non dérivé de l'URL).
  usePageReset(
    useCallback(() => {
      setChoices(new Map());
      setPage(0);
      setExactChoices(new Map());
      setExactPage(0);
      setThreshold(DEDUP_THRESHOLD_DEFAULT);
      setFeedback(null);
      // Purge toute réserve de défilement en attente : un reset explicite doit repartir de
      // zéro et ne JAMAIS restaurer un état périmé au prochain montage.
      clearScrollStash(DEDUP_SCROLL_KEY);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, []),
  );

  const onRun = useCallback(async () => {
    if (!libraryId || runBusy) return;
    setRunBusy(true);
    setFeedback(null);
    try {
      await runDedup(libraryId, { threshold, dryRun: false });
      recordAction('dedup.run');
      setFeedback({ kind: 'runQueued' });
    } catch {
      setFeedback({ kind: 'runError' });
    } finally {
      setRunBusy(false);
    }
  }, [libraryId, runBusy, threshold]);

  const onRefresh = useCallback(() => {
    setPage(0);
    setChoices(new Map());
    setFeedback(null);
    setRefreshKey((k) => k + 1);
  }, []);

  const onConfirmApply = useCallback(async () => {
    if (!libraryId || applyBusy || decisions.length === 0) return;
    setApplyBusy(true);
    setFeedback(null);
    try {
      await applyDedup(libraryId, {
        decisions: decisions as [DedupDecision, ...DedupDecision[]],
        dryRun: false,
      });
      recordAction('dedup.apply', { deleted: deleteCount });
      setConfirmOpen(false);
      // NE PAS recharger tout de suite : le job n'a pas encore supprimé les fichiers, le
      // serveur renverrait les MÊMES membres. On arme l'observation du job et on recharge
      // à sa FIN (`useRefreshAfterJob` → `onApplyJobFinished`).
      markApplyPending();
      setFeedback({ kind: 'applyRunning' });
      setChoices(new Map());
    } catch (err) {
      if (err instanceof ApiError && err.status === 409 && err.code === 'WRITE_DISABLED') {
        setFeedback({ kind: 'writeDisabled' });
      } else {
        setFeedback({ kind: 'applyError' });
      }
      setConfirmOpen(false);
    } finally {
      setApplyBusy(false);
    }
  }, [libraryId, applyBusy, decisions, deleteCount, markApplyPending]);

  /**
   * Écarte un cluster « faux positif » (« Ce ne sont pas des doublons ») : masque le cluster
   * de la revue et du décompte jusqu'à la PROCHAINE ANALYSE (le job d'analyse purge+recrée les
   * clusters → le flag se réinitialise). Retrait OPTIMISTE (le cluster disparaît au clic), puis
   * rafraîchissement du résumé/liste à la confirmation ; en cas d'échec, on RÉTABLIT la carte
   * et on affiche un message (403 → « accès écriture requis »).
   */
  const dismissCluster = useCallback(
    async (clusterId: string) => {
      if (!libraryId || dismissingIds.has(clusterId)) return;
      setFeedback(null);
      setDismissingIds((prev) => new Set(prev).add(clusterId));
      // Retrait optimiste immédiat de la liste affichée.
      setDismissedIds((prev) => new Set(prev).add(clusterId));
      try {
        await dismissClusters(libraryId, [clusterId]);
        recordAction('dedup.dismiss', { clusterId });
        // Rafraîchit UNIQUEMENT le résumé (compteurs) — PAS la liste, qui reste pilotée par le
        // retrait optimiste (`dismissedIds`) jusqu'à un vrai rechargement (analyse/apply/page).
        setSummaryRefreshKey((k) => k + 1);
      } catch (err) {
        // Échec : on RÉTABLIT le cluster (retire du retrait optimiste) et on signale.
        setDismissedIds((prev) => {
          const next = new Set(prev);
          next.delete(clusterId);
          return next;
        });
        setFeedback({
          kind: err instanceof ApiError && err.status === 403 ? 'dismissForbidden' : 'dismissError',
        });
      } finally {
        setDismissingIds((prev) => {
          const next = new Set(prev);
          next.delete(clusterId);
          return next;
        });
      }
    },
    [libraryId, dismissingIds],
  );

  /**
   * Écrit la réserve de défilement (onglet + pages + décisions des deux onglets + scrollTop)
   * AVANT d'ouvrir la visionneuse : celle-ci est rendue sous le LAYOUT GALERIE (pas sous
   * cette route) → cet écran va se DÉMONTER puis se remonter à neuf au retour ; la réserve
   * permet de retomber au même endroit, sur le bon onglet, avec les mêmes décisions.
   */
  const saveStash = useCallback(() => {
    saveScrollStash<DedupStashExtra>(DEDUP_SCROLL_KEY, scrollRef.current, {
      tab,
      similarPage: page,
      similarChoices: serializeSimilar(choices),
      exactPage,
      exactChoices: [...exactChoices],
    });
  }, [tab, page, choices, exactPage, exactChoices]);

  // Ouvre un cluster « similaire » comme sa propre série dans la visionneuse.
  const openMember = useCallback(
    (cluster: DedupCluster, assetId: string) => {
      saveStash();
      openViewerSeries(
        navigate,
        cluster.members.map((m) => m.assetId),
        assetId,
        '/outils/dedup',
      );
    },
    [navigate, saveStash],
  );

  // Ouvre une image « copie identique » dans la visionneuse (série à 1 élément).
  const openExact = useCallback(
    (assetIds: string[], startId: string) => {
      saveStash();
      openViewerSeries(navigate, assetIds, startId, '/outils/dedup');
    },
    [navigate, saveStash],
  );

  const total = clustersState.data?.total ?? 0;
  const lastRunAt = summaryState.data?.lastRunAt ?? null;
  const hasClusters = (clusters?.length ?? 0) > 0;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* En-tête de page. */}
      <div className="flex items-center justify-between gap-2 border-b border-border pb-4">
        <h1 className="flex items-center gap-2 text-lg font-semibold text-fg">
          <Square2StackIcon className="h-6 w-6" aria-hidden="true" />
          {t('dedup.title')}
        </h1>
        <button
          type="button"
          onClick={onClose}
          title={t('dedup.close')}
          aria-label={t('dedup.close')}
          className="rounded-md p-2 text-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowUturnLeftIcon className="h-6 w-6" aria-hidden="true" />
        </button>
      </div>

      {/* Onglets : Copies identiques / Images similaires. */}
      <div
        role="tablist"
        aria-label={t('dedup.tabsLabel')}
        onKeyDown={onTabsKeyDown}
        className="flex gap-1 overflow-x-auto border-b border-border"
      >
        <TabButton
          active={tab === 'identiques'}
          onClick={() => setTab('identiques')}
          controls="dedup-tab-identiques"
          id="dedup-tabbtn-identiques"
        >
          <DocumentDuplicateIcon className="h-5 w-5" aria-hidden="true" />
          {t('dedup.tabExact')}
        </TabButton>
        <TabButton
          active={tab === 'similaires'}
          onClick={() => setTab('similaires')}
          controls="dedup-tab-similaires"
          id="dedup-tabbtn-similaires"
        >
          <Square2StackIcon className="h-5 w-5" aria-hidden="true" />
          {t('dedup.tabSimilar')}
        </TabButton>
      </div>

      {/* Corps défilant (le seul conteneur de défilement de la page).
          `relative` — IMPÉRATIF : les radios masquées `sr-only` (position:absolute) de la
          section « Images similaires » ont pour bloc conteneur l'ancêtre positionné le plus
          proche. Sans `relative` ici, ce bloc conteneur est le DOCUMENT → des centaines
          d'inputs absolus gonflent sa hauteur (zone blanche fantôme, barre externe parasite,
          saut de scroll au focus d'un radio). Bug de layout visible en navigateur réel
          uniquement (jsdom ne calcule pas la mise en page → invisible aux tests).

          `ref={scrollRef}` — cible de la restauration « endroit dans la liste » au retour
          de la visionneuse (cf. `useScrollRestore` / `saveScrollStash` plus haut). */}
      <div ref={scrollRef} className="relative min-h-0 flex-1 overflow-y-auto py-4">
        <div className="mx-auto flex max-w-5xl flex-col gap-6">
          {/* Onglet « Copies identiques ». */}
          <div
            role="tabpanel"
            id="dedup-tab-identiques"
            aria-labelledby="dedup-tabbtn-identiques"
            hidden={tab !== 'identiques'}
          >
            {tab === 'identiques' && (
              <ExactCopiesSection
                libraryId={libraryId}
                page={exactPage}
                onPageChange={setExactPage}
                choices={exactChoices}
                onChoicesChange={setExactChoices}
                onOpenViewer={openExact}
                onReadyChange={setExactReady}
              />
            )}
          </div>

          {/* Onglet « Images similaires » (quasi-doublons pHash). */}
          <div
            role="tabpanel"
            id="dedup-tab-similaires"
            aria-labelledby="dedup-tabbtn-similaires"
            hidden={tab !== 'similaires'}
          >
            {tab === 'similaires' && (
              <section aria-labelledby="similar-heading" className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <h2
                    id="similar-heading"
                    className="flex items-center gap-2 text-base font-semibold text-fg"
                  >
                    <Square2StackIcon className="h-5 w-5" aria-hidden="true" />
                    {t('dedup.similarTitle')}
                  </h2>
                  <p className="max-w-2xl text-sm text-muted">{t('dedup.intro')}</p>
                </div>

                <AnalysisHeader
                  clusterCount={summaryState.data?.clusterCount ?? 0}
                  duplicateCount={summaryState.data?.duplicateCount ?? 0}
                  lastRunAt={lastRunAt}
                  threshold={threshold}
                  onThresholdChange={setThreshold}
                  runBusy={runBusy}
                  onRun={() => void onRun()}
                  onRefresh={onRefresh}
                />

                {feedback && <FeedbackBanner feedback={feedback} />}

                {clustersState.status === 'error' ? (
                  <div className="flex flex-col items-center gap-3 py-12 text-center" role="alert">
                    <ExclamationTriangleIcon className="h-10 w-10 text-danger" aria-hidden="true" />
                    <p className="text-fg">{t('dedup.errorTitle')}</p>
                    <button
                      type="button"
                      onClick={clustersState.reload}
                      className="rounded-md bg-brand px-3 py-2 text-sm font-medium text-brand-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
                    >
                      {t('dedup.retry')}
                    </button>
                  </div>
                ) : clustersState.status === 'loading' || clustersState.status === 'idle' ? (
                  <p className="py-12 text-center text-muted" role="status">
                    {t('dedup.loading')}
                  </p>
                ) : hasClusters ? (
                  <>
                    <ul className="flex flex-col gap-6">
                      {clusters!.map((cluster) => (
                        <ClusterCard
                          key={cluster.id}
                          cluster={cluster}
                          keptCount={keptCountOf(cluster)}
                          dismissing={dismissingIds.has(cluster.id)}
                          decisionOf={(assetId) => decisionOf(cluster.id, assetId)}
                          onDecide={(assetId, action) => setDecision(cluster.id, assetId, action)}
                          onOpen={(assetId) => openMember(cluster, assetId)}
                          onDismiss={() => void dismissCluster(cluster.id)}
                        />
                      ))}
                    </ul>

                    <Pagination
                      page={page}
                      limit={limit}
                      total={total}
                      loaded={clusters!.length}
                      onPrev={() => setPage((p) => Math.max(0, p - 1))}
                      onNext={() => setPage((p) => p + 1)}
                    />

                    {/* Barre d'application (collée au bas du flux). */}
                    <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
                      <button
                        type="button"
                        // ⚠ `applyInProgress` : après `setChoices(new Map())` les décisions
                        // sont vidées, mais tant que la liste (périmée) n'est pas rechargée un
                        // nouveau clic ré-enfilerait la même suppression. Cette garde EXPLICITE
                        // empêche le double-envoi pendant que le job tourne encore.
                        disabled={deleteCount === 0 || applyBusy || applyInProgress}
                        onClick={() => setConfirmOpen(true)}
                        className="inline-flex items-center gap-2 rounded-md bg-danger px-4 py-2 text-sm font-medium text-danger-fg disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-danger"
                      >
                        <TrashIcon className="h-5 w-5" aria-hidden="true" />
                        {t('dedup.applyButton')}
                      </button>
                      {applyInProgress ? (
                        <p className="inline-flex items-center gap-2 text-sm text-fg" role="status">
                          <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
                          {t('dedup.applyRunning')}
                        </p>
                      ) : (
                        <p className="text-sm text-muted" aria-live="polite">
                          {deleteCount > 0
                            ? tp('dedup.selectedForDeletion', deleteCount)
                            : t('dedup.applyNone')}
                        </p>
                      )}
                    </div>
                  </>
                ) : (
                  <EmptyState hasRun={lastRunAt !== null} />
                )}
              </section>
            )}
          </div>
        </div>
      </div>

      {confirmOpen && (
        <Portal>
          <DedupConfirmModal
            count={deleteCount}
            busy={applyBusy}
            onConfirm={() => void onConfirmApply()}
            onClose={() => setConfirmOpen(false)}
          />
        </Portal>
      )}
    </div>
  );
}

/** Un onglet accessible (`role=tab`) : actif signalé par soulignement + `aria-selected` + icône. */
function TabButton({
  active,
  onClick,
  controls,
  id,
  children,
}: {
  active: boolean;
  onClick: () => void;
  controls: string;
  id: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      id={id}
      aria-selected={active}
      aria-controls={controls}
      tabIndex={rovingTabIndex(active)}
      onClick={onClick}
      className={`-mb-px inline-flex min-h-11 shrink-0 items-center gap-2 whitespace-nowrap border-b-2 px-4 py-2.5 text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand ${
        active
          ? 'border-brand text-brand'
          : 'border-transparent text-muted hover:border-border hover:text-fg'
      }`}
    >
      {children}
    </button>
  );
}

/** En-tête : résumé + lancement/actualisation de l'analyse. */
function AnalysisHeader({
  clusterCount,
  duplicateCount,
  lastRunAt,
  threshold,
  onThresholdChange,
  runBusy,
  onRun,
  onRefresh,
}: {
  clusterCount: number;
  duplicateCount: number;
  lastRunAt: string | null;
  threshold: number;
  onThresholdChange: (v: number) => void;
  runBusy: boolean;
  onRun: () => void;
  onRefresh: () => void;
}): React.JSX.Element {
  const { t, tp, locale } = useI18n();
  const when =
    lastRunAt !== null
      ? new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
          new Date(lastRunAt),
        )
      : null;

  return (
    <section
      aria-labelledby="dedup-header-heading"
      className="flex flex-col gap-4 rounded-lg border border-border p-4"
    >
      <h2 id="dedup-header-heading" className="sr-only">
        {t('dedup.title')}
      </h2>
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        {when !== null ? (
          <>
            {/* Nombre d'images en doublons EN TÊTE : en gras si non nul, discret si zéro. */}
            <span className={duplicateCount > 0 ? 'font-semibold text-fg' : 'text-muted'}>
              {tp('dedup.summaryDuplicates', duplicateCount)}
            </span>
            <span className="text-muted">{tp('dedup.summaryClusters', clusterCount)}</span>
            <span className="text-muted">{interpolate(t('dedup.lastRunAt'), { when })}</span>
          </>
        ) : (
          <span className="font-semibold text-fg">{t('dedup.neverRun')}</span>
        )}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label htmlFor="dedup-threshold" className="text-sm font-medium text-fg">
            {t('dedup.thresholdLabel')}
          </label>
          <input
            id="dedup-threshold"
            type="number"
            min={DEDUP_THRESHOLD_MIN}
            max={DEDUP_THRESHOLD_MAX}
            value={threshold}
            onChange={(e) => {
              const n = Number.parseInt(e.target.value, 10);
              if (Number.isFinite(n)) {
                onThresholdChange(Math.min(DEDUP_THRESHOLD_MAX, Math.max(DEDUP_THRESHOLD_MIN, n)));
              }
            }}
            className="h-11 w-24 rounded-md border border-border bg-surface px-3 text-sm text-fg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
          />
          <span className="text-xs text-muted">{t('dedup.thresholdHint')}</span>
        </div>

        <button
          type="button"
          disabled={runBusy}
          onClick={onRun}
          className="inline-flex h-11 items-center gap-2 rounded-md bg-brand px-4 text-sm font-medium text-brand-fg disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          <ArrowPathIcon
            className={`h-5 w-5 ${runBusy ? 'animate-spin' : ''}`}
            aria-hidden="true"
          />
          {runBusy ? t('dedup.running') : t('dedup.runAnalysis')}
        </button>

        <button
          type="button"
          onClick={onRefresh}
          className="inline-flex h-11 items-center gap-2 rounded-md border border-border px-4 text-sm font-medium text-fg hover:bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {t('dedup.refresh')}
        </button>
      </div>
    </section>
  );
}

/** Bannière de retour (analyse enfilée, suppression en cours/enfilée, ou erreur). */
function FeedbackBanner({ feedback }: { feedback: Feedback }): React.JSX.Element {
  const { t } = useI18n();
  const isError =
    feedback.kind === 'runError' ||
    feedback.kind === 'applyError' ||
    feedback.kind === 'writeDisabled' ||
    feedback.kind === 'dismissError' ||
    feedback.kind === 'dismissForbidden';
  const isRunning = feedback.kind === 'applyRunning';
  const message =
    feedback.kind === 'runQueued'
      ? t('dedup.analysisQueued')
      : feedback.kind === 'applyRunning'
        ? t('dedup.applyRunning')
        : feedback.kind === 'applyQueued'
          ? t('dedup.applyQueued')
          : feedback.kind === 'writeDisabled'
            ? t('dedup.errorWriteDisabled')
            : feedback.kind === 'dismissForbidden'
              ? t('dedup.dismissForbidden')
              : feedback.kind === 'dismissError'
                ? t('dedup.dismissError')
                : feedback.kind === 'applyError'
                  ? t('dedup.errorApply')
                  : t('dedup.errorRun');
  return (
    <p
      role={isError ? 'alert' : 'status'}
      className={`inline-flex items-start gap-2 rounded-md border p-3 text-sm ${
        isError ? 'border-danger/40 bg-surface-2 text-danger' : 'border-border bg-surface-2 text-fg'
      }`}
    >
      {isError ? (
        <ExclamationTriangleIcon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
      ) : isRunning ? (
        <ArrowPathIcon
          className="mt-0.5 h-5 w-5 shrink-0 animate-spin text-brand"
          aria-hidden="true"
        />
      ) : (
        <CheckCircleIcon className="mt-0.5 h-5 w-5 shrink-0 text-brand" aria-hidden="true" />
      )}
      <span>{message}</span>
    </p>
  );
}

/**
 * Carte d'un cluster : ses membres côte à côte, CHACUN avec sa décision exclusive
 * Conserver / Effacer. Le garde-fou « ≥ 1 conservé » désactive l'effacement du dernier
 * membre encore conservé (`keptCount === 1`).
 */
function ClusterCard({
  cluster,
  keptCount,
  dismissing,
  decisionOf,
  onDecide,
  onOpen,
  onDismiss,
}: {
  cluster: DedupCluster;
  keptCount: number;
  dismissing: boolean;
  decisionOf: (assetId: string) => DedupAction;
  onDecide: (assetId: string, action: DedupAction) => void;
  onOpen: (assetId: string) => void;
  onDismiss: () => void;
}): React.JSX.Element {
  const { t } = useI18n();
  return (
    <li className="rounded-lg border border-border bg-surface p-4">
      {/* Barre de carte : action « Ce ne sont pas des doublons » (écarte le faux positif). */}
      <div className="mb-3 flex justify-end">
        <button
          type="button"
          disabled={dismissing}
          onClick={onDismiss}
          className="inline-flex items-center gap-1.5 rounded-md border border-border px-2.5 py-1.5 text-xs font-medium text-fg hover:bg-surface-2 disabled:cursor-not-allowed disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
        >
          {dismissing ? (
            <ArrowPathIcon className="h-4 w-4 animate-spin" aria-hidden="true" />
          ) : (
            <HandThumbUpIcon className="h-4 w-4" aria-hidden="true" />
          )}
          {dismissing ? t('dedup.dismissing') : t('dedup.notDuplicates')}
        </button>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {cluster.members.map((member) => {
          const decision = decisionOf(member.assetId);
          const isKept = decision === 'keep';
          // Garde-fou : on ne peut pas effacer le DERNIER membre encore conservé.
          const eraseDisabled = isKept && keptCount === 1;
          return (
            <MemberTile
              key={member.assetId}
              member={member}
              clusterId={cluster.id}
              decision={decision}
              eraseDisabled={eraseDisabled}
              onDecide={(action) => onDecide(member.assetId, action)}
              onOpen={() => onOpen(member.assetId)}
            />
          );
        })}
      </div>
    </li>
  );
}

/**
 * Une tuile membre : vignette CLIQUABLE (ouvre la série) + métadonnées + contrôle
 * exclusif Conserver / Effacer (radiogroup 2 options par membre).
 */
function MemberTile({
  member,
  clusterId,
  decision,
  eraseDisabled,
  onDecide,
  onOpen,
}: {
  member: DedupMember;
  clusterId: string;
  decision: DedupAction;
  eraseDisabled: boolean;
  onDecide: (action: DedupAction) => void;
  onOpen: () => void;
}): React.JSX.Element {
  const { t, locale } = useI18n();
  const resolution =
    member.width !== null && member.height !== null
      ? interpolate(t('dedup.resolution'), { width: member.width, height: member.height })
      : t('dedup.resolutionUnknown');
  const willDelete = decision === 'delete';
  const dir = dirnameOf(member.relPath);

  return (
    <div
      className={`flex flex-col gap-2 rounded-lg border p-2 ${
        willDelete ? 'border-danger/40' : 'border-border'
      }`}
    >
      {/* Vignette cliquable → ouvre l'image dans la visionneuse comme série du groupe. */}
      <button
        type="button"
        onClick={onOpen}
        aria-label={interpolate(t('dedup.openInViewer'), { name: member.filename })}
        className="relative aspect-square overflow-hidden rounded-md bg-surface-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        <img
          src={withSlotParam(`/api/assets/${member.assetId}/thumbnail?size=256`)}
          alt={member.filename}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          className="h-full w-full object-cover"
        />
        {member.kind === 'video' && (
          <span className="pointer-events-none absolute bottom-1 start-1 text-fg drop-shadow">
            <PlayCircleIcon className="h-5 w-5" aria-hidden="true" />
            <span className="sr-only">{t('badge.video')}</span>
          </span>
        )}
      </button>

      <div className="flex flex-col gap-1">
        {/* Chemin COMPLET vers la photo (dossier discret + nom de fichier en évidence),
            pas seulement le nom — le dossier donne le contexte, le nom reste lisible. */}
        <p className="break-all text-xs text-fg" title={member.relPath}>
          {dir !== '' && <span className="text-muted">{dir}/</span>}
          <span className="font-medium">{member.filename}</span>
        </p>
        <p className="text-xs text-muted">
          {resolution} · {formatBytes(member.sizeBytes, locale)}
          {member.durationS !== null ? ` · ${formatDuration(member.durationS)}` : ''}
        </p>

        {/* Badge indicatif « Original suggéré » (hint, non contraignant). */}
        {member.isKeeper && (
          <div className="flex flex-wrap gap-1">
            <span className="inline-flex items-center gap-1 rounded bg-surface-2 px-1.5 py-0.5 text-[11px] font-medium text-fg">
              <StarIcon className="h-3.5 w-3.5" aria-hidden="true" />
              {t('dedup.keeperSuggested')}
            </span>
          </div>
        )}
      </div>

      {/* Décision exclusive Conserver / Effacer (radiogroup à 2 options). */}
      <div
        role="radiogroup"
        aria-label={interpolate(t('dedup.decisionLabel'), { name: member.filename })}
        className="mt-auto grid grid-cols-2 gap-1"
      >
        <DecisionRadio
          name={`decision-${clusterId}-${member.assetId}`}
          checked={decision === 'keep'}
          onChange={() => onDecide('keep')}
          label={t('dedup.decisionKeep')}
          icon="keep"
        />
        <DecisionRadio
          name={`decision-${clusterId}-${member.assetId}`}
          checked={decision === 'delete'}
          onChange={() => onDecide('delete')}
          disabled={eraseDisabled}
          disabledHint={eraseDisabled ? t('dedup.lastKeptHint') : undefined}
          label={t('dedup.decisionDelete')}
          icon="delete"
        />
      </div>
    </div>
  );
}

/** Une option Conserver/Effacer : radio native + icône + texte (jamais la couleur seule). */
function DecisionRadio({
  name,
  checked,
  onChange,
  disabled,
  disabledHint,
  label,
  icon,
}: {
  name: string;
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
  disabledHint?: string | undefined;
  label: string;
  icon: 'keep' | 'delete';
}): React.JSX.Element {
  const active = icon === 'delete';
  return (
    // `relative` : ancre la radio native `sr-only` (position:absolute) à CE label (déjà à
    // l'écran) plutôt qu'au document → focus au clic ne peut plus provoquer de saut de
    // scroll vers une position fantôme profonde. Voir le commentaire du corps défilant.
    <label
      title={disabled ? disabledHint : undefined}
      className={`relative inline-flex items-center justify-center gap-1 rounded-md border px-2 py-1.5 text-xs font-medium ${
        disabled
          ? 'cursor-not-allowed border-border text-muted opacity-60'
          : checked
            ? active
              ? 'border-danger bg-danger/10 text-danger'
              : 'border-brand bg-brand/10 text-brand'
            : 'border-border text-fg hover:bg-surface-2'
      }`}
    >
      <input
        type="radio"
        name={name}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        className="sr-only"
      />
      {icon === 'keep' ? (
        <CheckIcon className="h-4 w-4" aria-hidden="true" />
      ) : (
        <TrashIcon className="h-4 w-4" aria-hidden="true" />
      )}
      <span>{label}</span>
      {disabled && disabledHint && <span className="sr-only">{disabledHint}</span>}
    </label>
  );
}

/** Pagination précédent/suivant des clusters. */
function Pagination({
  page,
  limit,
  total,
  loaded,
  onPrev,
  onNext,
}: {
  page: number;
  limit: number;
  total: number;
  loaded: number;
  onPrev: () => void;
  onNext: () => void;
}): React.JSX.Element | null {
  const { t } = useI18n();
  if (total <= limit) return null;
  const from = page * limit + 1;
  const to = page * limit + loaded;
  const hasPrev = page > 0;
  const hasNext = to < total;

  return (
    <div className="flex items-center justify-between gap-3">
      <button
        type="button"
        disabled={!hasPrev}
        onClick={onPrev}
        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('dedup.prevPage')}
      </button>
      <p className="text-sm text-muted" aria-live="polite">
        {interpolate(t('dedup.pageStatus'), { from, to, total })}
      </p>
      <button
        type="button"
        disabled={!hasNext}
        onClick={onNext}
        className="rounded-md border border-border px-3 py-2 text-sm font-medium text-fg hover:bg-surface-2 disabled:opacity-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand"
      >
        {t('dedup.nextPage')}
      </button>
    </div>
  );
}

/** État vide : « jamais analysé » vs « aucun doublon détecté ». */
function EmptyState({ hasRun }: { hasRun: boolean }): React.JSX.Element {
  const { t } = useI18n();
  return (
    <div className="flex flex-col items-center gap-2 py-16 text-center text-muted">
      <Square2StackIcon className="h-10 w-10" aria-hidden="true" />
      <p className="font-medium text-fg">
        {hasRun ? t('dedup.emptyNoDuplicates') : t('dedup.emptyNeverRun')}
      </p>
    </div>
  );
}

// --- Chargement de données (hooks locaux, mirroir de useExifAnalysis) ---------

type LoadStatus = 'idle' | 'loading' | 'error' | 'ready';

function useClusters(
  libraryId: string,
  limit: number,
  offset: number,
  refreshKey: number,
): {
  status: LoadStatus;
  data: Awaited<ReturnType<typeof fetchClusters>> | null;
  reload: () => void;
} {
  const [status, setStatus] = useState<LoadStatus>('idle');
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchClusters>> | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const controllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    if (!libraryId) {
      controllerRef.current?.abort();
      setStatus('idle');
      setData(null);
      return;
    }
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setStatus('loading');
    fetchClusters(libraryId, limit, offset, controller.signal)
      .then((d) => {
        if (controller.signal.aborted) return;
        setData(d);
        setStatus('ready');
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setStatus('error');
      });
    return () => controller.abort();
  }, [libraryId, limit, offset, refreshKey, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);
  return { status, data, reload };
}

function useSummary(
  libraryId: string,
  refreshKey: number,
): { data: Awaited<ReturnType<typeof fetchSummary>> | null } {
  const [data, setData] = useState<Awaited<ReturnType<typeof fetchSummary>> | null>(null);

  useEffect(() => {
    if (!libraryId) {
      setData(null);
      return;
    }
    const controller = new AbortController();
    fetchSummary(libraryId, controller.signal)
      .then((d) => {
        if (!controller.signal.aborted) setData(d);
      })
      .catch(() => {
        /* résumé optionnel : silencieux (l'écran fonctionne sans) */
      });
    return () => controller.abort();
  }, [libraryId, refreshKey]);

  return { data };
}
